import type { SpecSummary } from '../types/board.js';

export type SpecLinkType = 'strategy' | 'goal';

const BASE_URL = '/api/spec-links';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export interface SpecLink {
  id: number;
  user_id: number;
  link_type: SpecLinkType;
  link_id: number;
  project_id: number;
  spec_id: string;
  created_at: string;
}

export interface LinkedSpecResult {
  link: SpecLink;
  spec: SpecSummary;
}

export async function getLinkedSpecs(linkType: SpecLinkType, linkId: number): Promise<LinkedSpecResult[]> {
  return fetchJson<LinkedSpecResult[]>(`${BASE_URL}/${linkType}/${linkId}`);
}

export async function createLink(
  linkType: SpecLinkType,
  linkId: number,
  projectId: number,
  specId: string,
): Promise<SpecLink> {
  return fetchJson<SpecLink>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linkType, linkId, projectId, specId }),
  });
}

export async function deleteLink(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
}

export async function getBackLinks(projectId: number, specId: string): Promise<SpecLink[]> {
  return fetchJson<SpecLink[]>(`${BASE_URL}/back/${projectId}/${encodeURIComponent(specId)}`);
}
